import { GridRowSelectionModel } from '@mui/x-data-grid';

import { useMutation, useQuery } from '@tanstack/react-query';

import {
  createEvaluation,
  deleteEvaluation,
  getEvaluations,
  getEvaluation,
  previewEvaluation,
  updateEvaluation,
  evaluationTitleExists,
  deleteEvaluationPosts,
  getEvaluationReport,
  getEvaluationStatistics,
  exportEvaluationCSV,
  getAuthors
} from '@/api/evaluations';
import { Evaluation } from '@/models/evaluations';
import { UnknownObject } from '@/models/misc';

import { Callback } from '.';

type onSuccessType = (data: any) => void;
type onErrorType = (error: any) => void;

type CreateResponse = {
  id: number;
  status: 'done';
};
type EvaluationResponse = {
  id: number;
  name: string;
  row_created_at: string;
  row_updated_at: string;
};
export type PreviewResponse = {
  totalPosts: number;
  totalCreators: number;
  totalKeywords: number;
  totalKeywordGroups: number;
  sponsoredPosts: number;
  platforms: {
    instagram: {
      totalPosts: number;
      postTypes: {
        post: number;
        reel: number;
        story: number;
      };
    };
    tiktok: {
      totalPosts: number;
      postTypes: {
        post: number;
        story: number;
      };
    };
    youtube: {
      totalPosts: number;
      postTypes: {
        video: number;
        short: number;
        livestream: number;
      };
    };
  };
  keywordOccurrences: [
    {
      keyword: string;
      matchedPosts: number;
    }
  ];
};

export const useFetchEvaluations = (
  params?: Evaluation.ListParams,
  onSuccess?: onSuccessType,
  onError?: onErrorType
) =>
  useQuery({
    queryKey: ['evaluations', params],
    queryFn: async () => {
      const result = await getEvaluations(params);
      return result.data;
    },
    placeholderData: (previousData) => previousData,
    ...(onSuccess && { onSuccess }),
    ...(onError && { onError })
  });

export const useFetchAuthors = () =>
  useQuery({
    queryKey: ['authors'],
    queryFn: async () => {
      const result = await getAuthors();
      return result.data;
    }
  });

type FetchEvaluation = {
  id: number;
  onSuccess?: Callback;
  onError?: Callback;
  refetchInterval?: number;
  enabled?: boolean;
};

export const useFetchEvaluation = ({
  id,
  onSuccess,
  onError,
  refetchInterval,
  enabled = true
}: FetchEvaluation) =>
  useQuery({
    queryKey: ['evaluation', id],
    queryFn: async () => {
      const result = await getEvaluation(id);
      return result.data;
    },
    refetchInterval,
    ...(onSuccess && { onSuccess }),
    ...(onError && { onError }),
    enabled
  });

export const useCreateEvaluation = (
  onSuccess: ({ data }: { data: CreateResponse }) => void,
  onError: Callback
) =>
  useMutation({
    mutationFn: createEvaluation,
    onSuccess,
    onError
  });

export const useUpdateEvaluation = (
  id: number,
  onSuccess: Callback,
  onError: Callback
) =>
  useMutation({
    mutationFn: (payload: UnknownObject) => updateEvaluation(id, payload),
    onSuccess,
    onError
  });

export const useDeleteEvaluation = (onSuccess: Callback, onError: Callback) =>
  useMutation({
    mutationFn: deleteEvaluation,
    onSuccess,
    onError
  });

export const usePreviewEvaluation = (params: UnknownObject, enabled: boolean) =>
  useQuery({
    queryKey: ['previewEvaluation'],
    queryFn: async ({ signal }) =>
      (await previewEvaluation(params, signal)).data,
    enabled,
    retry: false,
    gcTime: 0
  });

export const useEvaluationTitleExists = (
  onSuccess: ({ data }: { data: EvaluationResponse }) => void,
  onError?: Callback
) =>
  useMutation({
    mutationFn: evaluationTitleExists,
    onSuccess,
    onError
  });

export const useDeleteEvaluationPosts = (
  onSuccess: Callback,
  onError: Callback
) =>
  useMutation({
    mutationFn: deleteEvaluationPosts,
    onSuccess,
    onError
  });

type EvaluationReportProps = {
  id: number;
  onSuccess: onSuccessType;
  onError: onErrorType;
};

export const useGetEvaluationReport = ({
  id,
  onSuccess,
  onError
}: EvaluationReportProps) =>
  useMutation({
    mutationFn: (payload: UnknownObject) =>
      getEvaluationReport(id, payload.params, {
        postsIds: payload.postsIds
      }),
    onSuccess,
    onError
  });

type EvaluationStatisticsProps = {
  id: number;
  onSuccess?: onSuccessType;
  onError?: onErrorType;
  params: Evaluation.StatisticsParams;
};

export const useFetchEvaluationStatistics = ({
  id,
  onSuccess,
  onError,
  params
}: EvaluationStatisticsProps) =>
  useQuery({
    queryKey: ['evaluationStatistics', id, params],
    queryFn: async () => {
      const result = await getEvaluationStatistics(id, params);
      return result.data;
    },
    meta: {
      onSuccess,
      onError
    }
  });

interface ExportEvaluationPostsProps {
  onSuccess: Callback;
  onError: Callback;
  params: Evaluation.FilteringParams;
}

export const useExportEvaluationCSV = ({
  onSuccess,
  onError,
  params
}: ExportEvaluationPostsProps) =>
  useMutation({
    mutationKey: ['exportEvaluationPosts', params],
    mutationFn: (payload: { postIds: GridRowSelectionModel }) =>
      exportEvaluationCSV(params, payload),
    onSuccess,
    onError
  });
